<script>
  import { getContext, onMount } from "svelte";

  import { Checkbox } from "carbon-components-svelte";
  import {
    PasswordInput,
    TextInput,
    Select,
    Button,
  } from "paxray-components-svelte";

  import { userStores } from "@stores/index.js";
  import { UserService } from "@services/index.js";
  import { settingStores } from "@stores/settingStores.js";
  import Modal from "lib/components/Modal.svelte";
  import MultiSelect from "lib/components/MultiSelect.svelte";

  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import { CHECK_ICON, EDIT_ICON, SAVE_ICON } from "@utils/icons.js";

  export let user = {
    id: null,
    username: null,
    email: null,
    role: null,
    password: "",
    pwd_enabled: false,
    teams: [],
    groups: [],
  };

  export let logedUser = false;
  export let accountOnly = false;

  export let modalOpen = false;
  let userLoaded = false;
  let permissions = [];
  const role = getContext("role");
  $: canManageUsers =
    $role?.role === "admin" || permissions.includes("manage_users");

  const userToken = localStorage.getItem("access_token");

  $: ({ teams, groups, roles } = $settingStores);
  const { getTeams, getGroups, getAvailableValues, updateUser, addUser } =
    settingStores;

  let testUserModal = {
    username: false,
    email: false,
    password: false,
    confirmPassword: false,
    save: false,
  };

  let changePassword = false;
  let confirmPassword = "";
  let editingField = null;
  let savedField = null;
  let passwordModalOpen = false;
  let passwordSaveAttempted = false;
  const userService = new UserService();
  const setUserInstore = () => {
    userService
      .getLoggedInUser()
      .then((user) => {
        if (user && user?.id) {
          userStores.set(user);
        }
      })
      .catch((error) => {
        session.invalidate();
      });
  };

  $: if (canManageUsers && !accountOnly) {
    getModalData();
  }

  function getModalData() {
    if (teams.length === 0) getTeams();
    if (roles.length === 0) getAvailableValues();
    if (groups.length === 0) getGroups();
  }

  $: showPasswordFields = !user.id || (user.pwd_enabled && changePassword);
  $: incorrectPassword =
    showPasswordFields && user.password !== confirmPassword;
  $: passwordInputIncomplete =
    showPasswordFields && (!user.password || !confirmPassword);
  $: accountPasswordIncomplete = !user.password || !confirmPassword;
  $: accountPasswordMismatch = user.password !== confirmPassword;
  $: accountPasswordNotAllowed =
    accountPasswordIncomplete || accountPasswordMismatch;

  $: disablePasswordInput =
    (incorrectPassword && testUserModal.save) ||
    (!user.id && testUserModal.password && !user.password) ||
    (testUserModal.password &&
      testUserModal.confirmPassword &&
      !user.password &&
      confirmPassword);

  $: disableConfirmPasswordInput =
    (incorrectPassword && testUserModal.save) ||
    (!user.id && user.confirmPassword && !confirmPassword) ||
    (testUserModal.confirmPassword &&
      testUserModal.password &&
      user.password &&
      !confirmPassword);

  $: permissions = setPermissions($userStores.groups);
  //$: setPermissions($userStores.groups);

  $: if (user.id && (!user.pwd_enabled || !changePassword)) {
    user.password = "";
    confirmPassword = "";
    passwordSaveAttempted = false;
  }
  $: disabledModalButton =
    !(
      user.username &&
      user.email &&
      isValidEmail(user.email) &&
      user.role &&
      user.role !== "defaultRole"
    ) || incorrectPassword;

  const createUser = () => {
    if (
      incorrectPassword ||
      passwordInputIncomplete ||
      !isValidEmail(user.email)
    ) {
      testUserModal.save =
        incorrectPassword || passwordInputIncomplete ? true : false;
      testUserModal.email = !isValidEmail(user.email) ? true : false;
      return;
    }
    if (user.role === "defaultRole") return;
    fetchWithSession("/api/authentication/settings/users", {
      method: "POST",
      body: JSON.stringify(user),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.ok) addUser(data.data);
        modalOpen = false;
      })
      .catch(() => {
        modalOpen = false;
      });
  };

  function updateSesion() {
    fetchWithSession("/api/authentication/user/me", {
      method: "GET",
    })
      .then((response) => response.json())
      .then((data) => {
        userStores.set(data);
      });
  }

  function getAccountFieldIcon(field) {
    if (savedField === field) return "check";
    return editingField === field ? "save" : "edit";
  }

  function handleAccountFieldAction(field) {
    if (savedField === field) return;
    if (editingField !== field) {
      editingField = field;
      return;
    }
    saveAccountField(field);
  }

  function saveAccountField(field) {
    if (field === "username" && !user.username) {
      testUserModal.username = true;
      return;
    }
    if (field === "email" && !isValidEmail(user.email)) {
      testUserModal.email = true;
      return;
    }

    fetchWithSession("/api/authentication/user/update-me", {
      method: "PATCH",
      body: JSON.stringify({ [field]: user[field] }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.ok) {
          if (data.data?.id === $userStores.id) {
            updateSesion();
          }
          editingField = null;
          savedField = field;
          setTimeout(() => {
            if (savedField === field) savedField = null;
          }, 1000);
        }
      })
      .catch(() => {});
  }

  function saveAccountSettings() {
    if (!user.username) {
      testUserModal.username = true;
      return;
    }
    if (!isValidEmail(user.email)) {
      testUserModal.email = true;
      return;
    }

    fetchWithSession("/api/authentication/user/update-me", {
      method: "PATCH",
      body: JSON.stringify({
        username: user.username,
        email: user.email,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.ok) {
          if (data.data?.id === $userStores.id) {
            updateSesion();
          }
          modalOpen = false;
        }
      })
      .catch(() => {});
  }

  function openPasswordModal() {
    changePassword = true;
    passwordModalOpen = true;
  }

  function closePasswordModal() {
    passwordModalOpen = false;
    changePassword = false;
  }

  function saveAccountPassword() {
    passwordSaveAttempted = true;

    if (accountPasswordNotAllowed) return;

    fetchWithSession("/api/authentication/user/update-me", {
      method: "PATCH",
      body: JSON.stringify({
        password: user.password,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.ok) {
          closePasswordModal();
          if (data.data?.id === $userStores.id) {
            updateSesion();
          }
        }
      })
      .catch(() => {});
  }

  const checkUserToUpdate = () => {
    if (
      incorrectPassword ||
      passwordInputIncomplete ||
      !isValidEmail(user.email)
    ) {
      testUserModal.save =
        incorrectPassword || passwordInputIncomplete ? true : false;
      testUserModal.email = !isValidEmail(user.email) ? true : false;
      return;
    }

    if (user.role === "defaultRole") return;
    let userToUpdate = {
      //id: user.id,
      username: user.username,
      email: user.email,
      // role: user.role,
      // teams: user.teams,
      // groups: user.groups,
    };

    if (showPasswordFields && user.password && user.password.length > 0) {
      userToUpdate.password = user.password;
    }
    userToUpdate.pwd_enabled = user.pwd_enabled;

    if (canManageUsers && !accountOnly) {
      //userToUpdate.id = user.id;
      userToUpdate.role = user.role;
      userToUpdate.teams = user.teams;
      userToUpdate.groups = user.groups;
    }

    let url =
      canManageUsers && !accountOnly
        ? `/api/authentication/settings/users/${user.id}`
        : `/api/authentication/user/update-me`;
    fetchWithSession(url, {
      method: "PATCH",
      body: JSON.stringify(userToUpdate),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.ok) {
          updateUser(data.data);
          if (data.data.id === $userStores.id) {
            updateSesion();
          }
          modalOpen = false;
        }
      })
      .catch(() => {
        modalOpen = false;
      });
  };

  function setPermissions(groups) {
    const permissionsSet = [];
    if (!Array.isArray(groups)) {
      permissions = [];
      return;
    }

    groups
      ?.map((group) => group.permissions)
      .forEach((perms) => {
        perms.forEach((p) => {
          if (!permissionsSet.includes(p)) permissionsSet.push(p);
        });
      });

    return permissionsSet;
  }

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  onMount(() => {
    if (logedUser) {
      user = {
        id: $userStores.id,
        username: $userStores.username,
        email: $userStores.email,
        role: $userStores.role,
        password: "",
        pwd_enabled: $userStores.pwd_enabled,
        teams: $userStores.teams.map((t) => t.id),
        groups: $userStores.groups.map((g) => g.id),
      };
      userLoaded = true;
    }
  });
</script>

<Modal
  autowidth={true}
  preventCloseOnClickOutside
  bind:open={modalOpen}
  modalHeading={accountOnly
    ? "Account settings"
    : user.id
      ? `Edit User: ${user.username}`
      : "Create User"}
  actions
  width={653}
  primaryButtonText={user.id ? "Save" : "Create User"}
  secondaryButtonText={accountOnly ? "Close" : "Cancel"}
  primaryButtonAction={accountOnly
    ? saveAccountSettings
    : user.id
      ? checkUserToUpdate
      : createUser}
  primaryButtonNotAllow={accountOnly
    ? !user.username || !isValidEmail(user.email)
    : !user.username ||
      !user.email ||
      (showPasswordFields && !user.password) ||
      (showPasswordFields && !confirmPassword)}
>
  <div class="user_form_wrapper">
    {#if accountOnly}
      <div class="account-field-row">
        <div class="account-field-input">
          <label for="account-username">User name</label>
          <input
            id="account-username"
            autocomplete="off"
            disabled={editingField !== "username"}
            class:invalid={testUserModal.username && !user.username}
            on:change={() => {
              testUserModal.username = true;
            }}
            bind:value={user.username}
          />
          {#if testUserModal.username && !user.username}
            <span class="account-field-error">Please add a user name.</span>
          {/if}
        </div>
        <div class="account-field-action">
          <Button
            kind="secondary"
            size="field"
            icon={editingField === "username"
              ? SAVE_ICON
              : savedField === "username"
                ? CHECK_ICON
                : EDIT_ICON}
            iconDescription={editingField === "username"
              ? "Save user name"
              : "Edit user name"}
            on:click={() => handleAccountFieldAction("username")}
          />
        </div>
      </div>
      <div class="account-field-row">
        <div class="account-field-input">
          <label for="account-email">E-Mail</label>
          <input
            id="account-email"
            autocomplete="off"
            disabled={editingField !== "email"}
            class:invalid={testUserModal.email && !isValidEmail(user.email)}
            bind:value={user.email}
            on:change={() => {
              testUserModal.email = false;
            }}
          />
          {#if testUserModal.email && !isValidEmail(user.email)}
            <span class="account-field-error">Please add a valid email.</span>
          {/if}
        </div>
        <div class="account-field-action">
          <Button
            kind="secondary"
            size="field"
            icon={editingField === "email"
              ? SAVE_ICON
              : savedField === "email"
                ? CHECK_ICON
                : EDIT_ICON}
            iconDescription={editingField === "email"
              ? "Save e-mail"
              : "Edit e-mail"}
            on:click={() => handleAccountFieldAction("email")}
          />
        </div>
      </div>
      {#if user.pwd_enabled}
        <div class="change-password-button">
          <Button
            kind="secondary"
            size="field"
            text="Change password"
            on:click={openPasswordModal}
          />
        </div>
      {/if}
    {:else}
      <div class="user-field-row">
        <TextInput
          autocomplete="off"
          invalid={testUserModal.username && !user.username}
          invalidText="Please add a user name."
          labelText="User name"
          on:change={() => {
            testUserModal.username = true;
          }}
          bind:value={user.username}
        />
        <TextInput
          autocomplete="off"
          invalid={testUserModal.email && !isValidEmail(user.email)}
          invalidText="Please add a valid email."
          labelText="E-Mail"
          bind:value={user.email}
          on:change={() => {
            testUserModal.email = false;
          }}
        />
      </div>
    {/if}

    {#if !accountOnly}
      <div
        class:password-wrapper={true}
        class:invalid-password={(testUserModal.save &&
          showPasswordFields &&
          disablePasswordInput) ||
          (testUserModal.save &&
            showPasswordFields &&
            disableConfirmPasswordInput)}
      >
        {#if !accountOnly && user.id}
          <Checkbox
            id="passwordLoginEnabledCheckbox"
            disabled={!canManageUsers}
            labelText="Password login enabled"
            bind:checked={user.pwd_enabled}
            on:change={() => {
              changePassword = user.pwd_enabled;
            }}
          />
        {/if}
        {#if user.pwd_enabled && user.id}
          <Button
            disabled={!(
              canManageUsers ||
              accountOnly ||
              permissions.includes("enable_pwd")
            )}
            kind="secondary"
            size="field"
            text={changePassword
              ? "Cancel password change"
              : user.id
                ? "Change password"
                : "Set password"}
            on:click={() => {
              changePassword = !changePassword;
            }}
          />
        {/if}
        {#if showPasswordFields}
          <div class="password-fields-row">
            <PasswordInput
              autocomplete="new-password"
              labelText="New password"
              tooltipPosition="left"
              invalid={testUserModal.save &&
                showPasswordFields &&
                disablePasswordInput}
              invalidText={user.password !== confirmPassword
                ? "Passwords do not match."
                : "Please add a password."}
              on:change={() => {
                testUserModal.save = false;
                testUserModal.password = true;
                testUserModal.confirmPassword = true;
              }}
              bind:value={user.password}
            />
            <PasswordInput
              autocomplete="new-password"
              labelText="Confirm new password"
              tooltipPosition="left"
              invalid={testUserModal.save &&
                showPasswordFields &&
                disableConfirmPasswordInput}
              invalidText="Please confirm the password."
              on:change={() => {
                testUserModal.save = false;
                testUserModal.password = true;
                testUserModal.confirmPassword = true;
              }}
              bind:value={confirmPassword}
            />
          </div>
        {/if}
      </div>
    {/if}

    <!-- [
          { value: "defaultRole", text: "Role" },
          { value: "admin", text: "Admin" },
          { value: "modeler", text: "Modeler" },
          { value: "viewer", text: "Viewer" },
        ] -->
    {#if canManageUsers && !accountOnly}
      {#key userLoaded}
        {#if roles.length > 0}
          <Select
            items={roles}
            labelText="Role"
            bind:selected={user.role}
            invalid={user.role === "defaultRole"}
            invalidText="Please select the user role."
          />
        {/if}
        {#if groups.length > 0}
          <MultiSelect
            bind:selectedIds={user.groups}
            items={groups}
            labelText="Groups"
            label="Group"
          />
        {/if}
        {#if teams.length > 0}
          <MultiSelect
            bind:selectedIds={user.teams}
            items={teams}
            labelText="Teams (optional)"
            label="Team"
          />
        {/if}
      {/key}
    {/if}
  </div>
</Modal>

{#if accountOnly && passwordModalOpen}
  <Modal
    autowidth={true}
    preventCloseOnClickOutside
    bind:open={passwordModalOpen}
    modalHeading="Change password"
    actions
    primaryButtonText="Save password"
    primaryButtonAction={saveAccountPassword}
    primaryButtonNotAllow={accountPasswordNotAllowed}
    secondaryButtonAction={closePasswordModal}
  >
    <div class="password-wrapper">
      <PasswordInput
        tooltipPosition="left"
        autocomplete="new-password"
        labelText="New password"
        invalid={passwordSaveAttempted && !user.password}
        invalidText="Please add a new password."
        bind:value={user.password}
      />
      <PasswordInput
        tooltipPosition="left"
        autocomplete="new-password"
        labelText="Confirm new password"
        invalid={(passwordSaveAttempted && !confirmPassword) ||
          (!!confirmPassword && accountPasswordMismatch)}
        invalidText={accountPasswordMismatch
          ? "Passwords do not match."
          : "Please confirm the new password."}
        bind:value={confirmPassword}
      />
    </div>
  </Modal>
{/if}

<style>
  .account-field-row {
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    gap: 0.75rem;
    margin-bottom: 1rem;
    width: 100%;
  }

  .user-field-row,
  .password-fields-row {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;
    width: 100%;
  }

  .user-field-row {
    margin-bottom: 0.25rem;
  }

  .account-field-input {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-width: 0;
  }

  .account-field-input label {
    color: var(--pax-black);
    font-size: 0.875rem;
    font-weight: 400;
    margin-bottom: 0.5rem;
  }

  .account-field-action {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    flex: 0 0 2.5rem;
    height: 2.5rem;
    margin-bottom: 0;
  }

  .account-field-input input {
    background: var(--pax-white);
    border: 1px solid var(--pax-black-inactive);
    border-radius: 0.5rem;
    color: var(--pax-black);
    font-size: 0.875rem;
    height: 2.5rem;
    padding: 0 1rem;
    width: 100%;
  }

  .account-field-input input:disabled {
    background: var(--pax-light-gray);
    color: var(--pax-black-inactive);
  }

  .account-field-input input.invalid {
    border-color: var(--pax-orange);
  }

  .account-field-error {
    color: var(--pax-orange);
    font-size: 0.75rem;
    margin-top: 0.25rem;
  }

  .change-password-button {
    margin-top: 1rem;
  }

  .password-wrapper {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-top: 1rem;
  }

  @media (max-width: 40rem) {
    .user-field-row,
    .password-fields-row {
      grid-template-columns: 1fr;
    }
  }
</style>
